import React, { Suspense, lazy } from "react";
import { createBrowserRouter, RouterProvider, Navigate, Outlet } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import Layout from "../components/layout/Layout";
import LoadingSpinner from "../components/common/LoadingSpinner";
import ErrorBoundary from "../components/common/ErrorBoundary";

// Lazy loading de páginas para code splitting
const Login = lazy(() => import("../pages/Login"));
const Dashboard = lazy(() => import("../pages/Dashboard"));
const PlantsPage = lazy(() => import("../pages/Plants"));
const ProcessesPage = lazy(() => import("../pages/Processes"));
const RoomsPage = lazy(() => import("../pages/Rooms"));
const MachinesPage = lazy(() => import("../pages/Machines"));
const CatalogsPage = lazy(() => import("../pages/Catalogs"));
const UsersPage = lazy(() => import("../pages/Users"));
const ProfilePage = lazy(() => import("../pages/Profile"));
const SettingsPage = lazy(() => import("../pages/Settings"));
const TokenManagementPage = lazy(() => import("../pages/TokenManagement"));
const ActivityLogPage = lazy(() => import("../pages/ActivityLog"));
const TrashPage = lazy(() => import("../pages/Trash"));
const BulkCatalogUploadPage = lazy(() => import("../pages/BulkCatalogUpload"));
const ShiftsPage = lazy(() => import("../pages/Shifts"));
const ReportsPage = lazy(() => import("../pages/Reports"));

// Páginas temporales por crear
const HelpPage = lazy(() => import("../pages/Dashboard")); // Temporal: usar Dashboard
const NotFoundPage = lazy(() => import("../pages/Dashboard")); // Temporal: usar Dashboard

/**
 * Componente de ruta protegida
 * Verifica autenticación antes de permitir el acceso
 */
const ProtectedRoute = ({ children, requiredRole = null }) => {
  const { isAuthenticated, user, isLoading } = useAuth();

  // Mostrar loading mientras se inicializa la autenticación
  if (isLoading) {
    return <LoadingSpinner variant="centered" minHeight="100vh" />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (requiredRole && user?.role !== requiredRole) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

/**
 * Componente de ruta pública
 * Redirige a dashboard si ya está autenticado
 */
const PublicRoute = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  // Mostrar loading mientras se inicializa la autenticación
  if (isLoading) {
    return <LoadingSpinner variant="centered" minHeight="100vh" message="Iniciando aplicación..." />;
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

/**
 * Layout con manejo de errores
 */
const LayoutWithErrorBoundary = () => (
  <ErrorBoundary>
    <Layout>
      <Suspense fallback={<LoadingSpinner variant="centered" minHeight="60vh" />}>
        <Outlet />
      </Suspense>
    </Layout>
  </ErrorBoundary>
);

/**
 * Wrapper para páginas con Suspense
 */
const PageWrapper = ({ children }) => (
  <ErrorBoundary>
    <Suspense fallback={<LoadingSpinner variant="centered" minHeight="60vh" />}>{children}</Suspense>
  </ErrorBoundary>
);

/**
 * Configuración del router principal
 */
const router = createBrowserRouter([
  // Rutas públicas (sin autenticación)
  {
    path: "/login",
    element: (
      <PublicRoute>
        <PageWrapper>
          <Login />
        </PageWrapper>
      </PublicRoute>
    ),
    errorElement: <ErrorBoundary />,
  },

  // Rutas protegidas (requieren autenticación)
  {
    path: "/",
    element: (
      <ProtectedRoute>
        <LayoutWithErrorBoundary />
      </ProtectedRoute>
    ),
    errorElement: <ErrorBoundary />,
    children: [
      // Redirección raíz
      {
        index: true,
        element: <Navigate to="/dashboard" replace />,
      },

      // Dashboard
      {
        path: "dashboard",
        element: <Dashboard />,
      },

      // Jerarquía organizacional
      {
        path: "plants",
        children: [
          {
            index: true,
            element: <PlantsPage />,
          },
          {
            path: ":plantId",
            element: <PlantsPage />,
          },
          {
            path: ":plantId/processes",
            element: <ProcessesPage />,
          },
          {
            path: ":plantId/processes/:processId",
            element: <ProcessesPage />,
          },
          {
            path: ":plantId/processes/:processId/rooms",
            element: <RoomsPage />,
          },
          {
            path: ":plantId/processes/:processId/rooms/:roomId",
            element: <RoomsPage />,
          },
          {
            path: ":plantId/processes/:processId/rooms/:roomId/machines",
            element: <MachinesPage />,
          },
          {
            path: ":plantId/processes/:processId/rooms/:roomId/machines/:machineId",
            element: <MachinesPage />,
          },
          {
            path: ":plantId/processes/:processId/rooms/:roomId/machines/:machineId/catalogs",
            element: <CatalogsPage />,
          },
        ],
      },

      // Rutas directas para cada nivel (navegación rápida)
      {
        path: "processes",
        children: [
          {
            index: true,
            element: <ProcessesPage />,
          },
          {
            path: ":processId",
            element: <ProcessesPage />,
          },
        ],
      },

      {
        path: "rooms",
        children: [
          {
            index: true,
            element: <RoomsPage />,
          },
          {
            path: ":roomId",
            element: <RoomsPage />,
          },
        ],
      },

      {
        path: "machines",
        children: [
          {
            index: true,
            element: <MachinesPage />,
          },
          {
            path: ":machineId",
            element: <MachinesPage />,
          },
        ],
      },

      {
        path: "catalogs",
        children: [
          {
            index: true,
            element: <CatalogsPage />,
          },
          {
            path: ":catalogId",
            element: <CatalogsPage />,
          },
        ],
      },

      // Administración (solo admin)
      {
        path: "users",
        element: (
          <ProtectedRoute requiredRole="admin">
            <UsersPage />
          </ProtectedRoute>
        ),
      },

      {
        path: "tokens",
        element: (
          <ProtectedRoute requiredRole="admin">
            <TokenManagementPage />
          </ProtectedRoute>
        ),
      },

      {
        path: "settings",
        element: (
          <ProtectedRoute requiredRole="admin">
            <SettingsPage />
          </ProtectedRoute>
        ),
      },

      {
        path: "trash",
        element: (
          <ProtectedRoute requiredRole="admin">
            <TrashPage />
          </ProtectedRoute>
        ),
      },

      {
        path: "bulk-upload",
        element: (
          <ProtectedRoute requiredRole="admin">
            <BulkCatalogUploadPage />
          </ProtectedRoute>
        ),
      },

      // Planificación
      {
        path: "shifts",
        element: <ShiftsPage />,
      },

      {
        path: "reports",
        element: <ReportsPage />,
      },

      // Usuario
      {
        path: "profile",
        element: <ProfilePage />,
      },

      {
        path: "activity",
        element: <ActivityLogPage />,
      },

      {
        path: "help",
        element: <HelpPage />,
      },
    ],
  },

  // Ruta 404 - debe ir al final
  {
    path: "*",
    element: (
      <PageWrapper>
        <NotFoundPage />
      </PageWrapper>
    ),
  },
]);

/**
 * Componente principal de routing
 */
const AppRouter = () => {
  return (
    <ErrorBoundary
      fallback={(error, errorInfo, retry) => (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "100vh",
            padding: "20px",
            textAlign: "center",
          }}>
          <h1>Error de Routing</h1>
          <p>Ha ocurrido un error en la navegación de la aplicación.</p>
          <button
            onClick={retry}
            style={{
              padding: "10px 20px",
              marginTop: "20px",
              backgroundColor: "#d32f2f",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
            }}>
            Reintentar
          </button>
        </div>
      )}>
      <RouterProvider router={router} fallbackElement={<LoadingSpinner variant="backdrop" />} />
    </ErrorBoundary>
  );
};

export default AppRouter;

/**
 * Hook para navegación programática con verificación de permisos
 */
export const useAppNavigation = () => {
  const { user, isAdmin } = useAuth();

  const canAccessRoute = (path) => {
    // Rutas que requieren admin
    const adminRoutes = ["/users"];

    if (adminRoutes.some((route) => path.startsWith(route))) {
      return isAdmin;
    }

    return true;
  };

  const getDefaultRoute = () => {
    return "/dashboard";
  };

  const getHierarchyRoute = (level, ids = {}) => {
    const { plantId, processId, roomId, machineId } = ids;

    switch (level) {
      case "plants":
        return plantId ? `/plants/${plantId}` : "/plants";
      case "processes":
        return processId
          ? plantId
            ? `/plants/${plantId}/processes/${processId}`
            : `/processes/${processId}`
          : "/processes";
      case "rooms":
        return roomId
          ? plantId && processId
            ? `/plants/${plantId}/processes/${processId}/rooms/${roomId}`
            : `/rooms/${roomId}`
          : "/rooms";
      case "machines":
        return machineId
          ? plantId && processId && roomId
            ? `/plants/${plantId}/processes/${processId}/rooms/${roomId}/machines/${machineId}`
            : `/machines/${machineId}`
          : "/machines";
      case "catalogs":
        return "/catalogs";
      default:
        return "/dashboard";
    }
  };

  return {
    canAccessRoute,
    getDefaultRoute,
    getHierarchyRoute,
  };
};
